import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  moduleLayoutOptionsWithoutIcon,
  moduleTypeOptionsWithoutIcon,
} from "entities/modules/constants";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import {
  ContentBlocksModuleLayout,
  ModuleType,
  PromotionModuleLayout,
} from "entities/modules/types";
import React, { ChangeEvent, FC } from "react";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import Select from "shared/components/Form/Select";
import Switch from "shared/components/Form/Switch";
import TextField from "shared/components/Form/TextField";
import {
  COLOR_BLACK,
  COLOR_GREY_6,
  COLOR_GREY_8,
  COLOR_PRIMARY_MAIN,
} from "shared/theme/colors";
import { useGeneralData } from "../../../../stores/useGeneralData";
import { useStudiosData } from "../../../../stores/useStudiosData";
import {
  moduleConfigurationView,
  moduleIcons,
  moduleTypeOptions,
} from "./constants";
import { IconListContainer, IconWrapper } from "./styled";
import { assureValidLayout } from "./utils";

const General: FC = () => {
  const {
    moduleName,
    moduleType,
    adminNotes,
    moduleLayout,
    studioFilterEnabled,
    iconShown,
    icon,
    setModuleName,
    setModuleType,
    setAdminNotes,
    setModuleLayout,
    setBonusObtainType,
    setIconShown,
    setIcon,
  } = useGeneralData();

  const setTagBlocks = useTagBlocksStore((state) => state.setTagBlocks);
  const setSelectedStudios = useStudiosData(
    (state) => state.setSelectedStudios,
  );

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setModuleName(e.target.value);
  };

  const handleTypeChange = (value: string) => {
    setModuleType(value as ModuleType);
    assureValidLayout({
      moduleType: value as ModuleType,
      studioFilterEnabled,
      moduleLayout,
      setModuleLayout,
    });
    setTagBlocks([]);
    setSelectedStudios([]);
    setBonusObtainType(null);
  };

  const handleAdminNotesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAdminNotes(e.target.value);
  };

  const handleIconShownChange = (checked: boolean) => {
    setIconShown(checked);
    if (!checked) {
      setIcon(null);
    }
  };

  const allowIconSelection =
    moduleType &&
    !moduleTypeOptionsWithoutIcon.includes(moduleType) &&
    !moduleLayoutOptionsWithoutIcon.includes(
      moduleLayout as ContentBlocksModuleLayout | PromotionModuleLayout,
    );

  return (
    <Stack spacing="1rem">
      <ContentSection title="GENERAL">
        <Stack spacing="1rem">
          <TextField
            id="moduleName"
            label="Name"
            labelColor={COLOR_BLACK}
            placeholder="Enter module name"
            fullWidth
            required
            value={moduleName}
            onChange={handleNameChange}
          />
          <Select
            id="moduleType"
            label="Type"
            labelColor={COLOR_BLACK}
            placeholder="Select a type"
            disabledPlaceholder
            fullWidth
            required
            value={moduleType ?? undefined}
            handleChange={handleTypeChange}
            options={moduleTypeOptions.filter(
              (option) => option.value === ModuleType.Games,
            )}
          />
          <TextField
            id="adminNotes"
            label="Admin Notes"
            labelColor={COLOR_BLACK}
            placeholder="Enter notes"
            fullWidth
            value={adminNotes}
            onChange={handleAdminNotesChange}
          />
          {allowIconSelection && (
            <Stack spacing="0.5rem">
              <Switch
                label="Show icon"
                value={iconShown}
                onChange={handleIconShownChange}
              />
              {iconShown && (
                <>
                  <IconListContainer>
                    {moduleIcons.map(({ name, Component }) => (
                      <IconWrapper key={name} onClick={() => setIcon(name)}>
                        <Component
                          color={
                            icon === name ? COLOR_PRIMARY_MAIN : COLOR_GREY_6
                          }
                        />
                      </IconWrapper>
                    ))}
                  </IconListContainer>
                  <Typography color={COLOR_GREY_8} variant="bodyMRegular">
                    Select an icon to display next to the name
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </ContentSection>
      {moduleType ? moduleConfigurationView[moduleType] : null}
    </Stack>
  );
};

export default General;
